import { Component, EventEmitter, Output } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { TranslocoService } from '@jsverse/transloco';

import { SimpleDialogButtonColor } from 'app/components/simple-dialog.component';
import { AuthService } from 'app/providers/bridge-api/auth.service';
import {
  ActionItem,
  ActionPlanService,
  ActionTask,
  Status,
} from 'app/providers/storage-api/actionplan.service';
import { AlertService } from 'app/services/alert.service';
import { DialogService } from 'app/services/dialog.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { ActionPlanUtils } from 'app/utils/action-plan.utils';
import { ActionItemListFilter } from '../action-item-list-filter/action-item-list-filter.component';
import {
  ActionPlanDetailsTableTabBase,
  IDataItem,
} from '../../shared/action-plan-details-table-tab-base';

interface TaskDataItem extends IDataItem {
  itemId: string;
  itemName: string;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-action-plan-details-tasks-tab',
  templateUrl: './action-plan-details-tasks-tab.component.html',
  styleUrls: ['./action-plan-details-tasks-tab.component.scss'],
  standalone: false,
})
export class ActionPlanDetailsTasksTabComponent extends ActionPlanDetailsTableTabBase<TaskDataItem> {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  @Output() navigateToItem = new EventEmitter<string>();

  displayedColumns: string[] = [
    'icon',
    'name',
    'item_name',
    'due_date',
    'status',
    'assignee',
    'actions',
  ];

  isActionTaskDialogOpen = false;
  taskOnMenu: TaskDataItem;
  actionItemToDialog: ActionItem;
  actionTaskToDialog: ActionTask;

  constructor(
    authService: AuthService,
    private actionPlanService: ActionPlanService,
    private alertService: AlertService,
    private dialogService: DialogService,
    private translocoService: TranslocoService
  ) {
    super(authService);
  }

  onToggleAllRow(): void {
    this.toggleAllRow();
  }

  onToggleRow(task: TaskDataItem): void {
    this.toggleRow(task);
  }

  onSortChange(sort: Sort): void {
    this.sortChange(sort);
  }

  onSearch(value: string): void {
    this.search(value);
  }

  onOpenFilterModal(): void {
    this.openFilterModal();
  }

  onCloseFilterModal(filter: ActionItemListFilter | null): void {
    this.closeFilterModal(filter);
  }

  onOpenActionsMenu(task: TaskDataItem): void {
    this.taskOnMenu = task;
  }

  async onStatusChange(status: Status, task: TaskDataItem): Promise<void> {
    const originalStatus = task.status;

    const actionItem = this.actionPlan.action_items.find((i) => i.item_id === task.itemId);
    const actionTask = actionItem.action_tasks.find((t) => t.task_id === task.id);
    actionTask.status = status;

    try {
      await this.actionPlanService.updateActionTask(
        this.actionPlan.plan_id,
        task.itemId,
        actionTask
      );

      ActionPlanUtils.setChildCountsForItem(actionItem);

      task.status = status;

      task.isExpired = ActionPlanUtils.isExpired(actionTask);
      task.expiredTooltip = ActionPlanUtils.getExpiredLabel(actionTask);

      this.setDataSource();

      this.alertService.showToast(
        this.translocoService.translateObject('action-plan-details-tasks-tab.status-update-success')
      );
    } catch (error) {
      actionTask.status = originalStatus;

      this.alertService.showErrorToast(
        this.translocoService.translateObject(
          'action-plan-details-tasks-tab.status-update-failure'
        ),
        error
      );
    }
  }

  onEditTask(): void {
    const actionItem = this.actionPlan.action_items.find(
      (i) => i.item_id === this.taskOnMenu.itemId
    );

    this.actionItemToDialog = actionItem;
    this.actionTaskToDialog = actionItem.action_tasks.find((t) => t.task_id === this.taskOnMenu.id);

    this.isActionTaskDialogOpen = true;
  }

  async onDeleteTask(): Promise<void> {
    this.dialogService
      .showWarningPopup({
        title: this.translocoService.translate(
          'action-plan-details-tasks-tab.delete-confirmation-title',
          { name: this.taskOnMenu.name }
        ),
        content: this.translocoService.translate(
          'action-plan-details-tasks-tab.delete-confirmation-content'
        ),
        confirmTitle: this.translocoService.translate('action-plan-details-tasks-tab.delete-task'),
        cancelTitle: this.translocoService.translate(
          'action-plan-details-tasks-tab.delete-confirmation-cancel'
        ),
        confirmColor: SimpleDialogButtonColor.red,
      })
      .afterClosed()
      .subscribe(async (result) => {
        if (result) {
          try {
            await this.actionPlanService.deleteActionTask(
              this.actionPlan.plan_id,
              this.taskOnMenu.itemId,
              this.taskOnMenu.id
            );

            const actionItem = this.actionPlan.action_items.find(
              (i) => i.item_id === this.taskOnMenu.itemId
            );
            const actionTaskIndex = actionItem.action_tasks.findIndex(
              (t) => t.task_id === this.taskOnMenu.id
            );
            actionItem.action_tasks.splice(actionTaskIndex, 1);

            ActionPlanUtils.setChildCountsForItem(actionItem);

            const index = this.dataList.indexOf(this.taskOnMenu);
            this.dataList.splice(index, 1);

            this.setDataSource();

            this.countChange.emit();

            this.alertService.showToast(
              this.translocoService.translate('action-plan-details-tasks-tab.delete-success')
            );
          } catch (error) {
            this.alertService.showErrorToast(
              this.translocoService.translate('action-plan-details-tasks-tab.delete-failure'),
              error
            );
          }
        }
      });
  }

  onUpdateActionTask(actionTask: ActionTask): void {
    const actionItem = this.actionPlan.action_items.find(
      (i) => i.item_id === this.actionItemToDialog.item_id
    );
    const actionTaskIndex = actionItem.action_tasks.findIndex(
      (t) => t.task_id === actionTask.task_id
    );
    actionItem.action_tasks[actionTaskIndex] = actionTask;

    ActionPlanUtils.setChildCountsForItem(actionItem);

    const task = this.dataList.find((t) => t.id === actionTask.task_id);
    const taskIndex = this.dataList.indexOf(task);
    this.dataList[taskIndex] = {
      ...task,
      name: actionTask.name,
      dueDate: new Date(actionTask.due_date),
      assignee: actionTask.assignee_user_name,
      notes: actionTask.notes,
      isExpired: ActionPlanUtils.isExpired(actionTask),
      expiredTooltip: ActionPlanUtils.getExpiredLabel(actionTask),
    };

    this.setDataSource();
  }

  onCloseActionTaskDialog(): void {
    this.isActionTaskDialogOpen = false;
  }

  onNavigateToItem(itemId: string): void {
    this.navigateToItem.emit(itemId);
  }

  protected getDataList(): TaskDataItem[] {
    const tasks: TaskDataItem[] = [];

    (this.actionPlan.action_items ?? []).forEach((i) => {
      if (i.action_tasks?.length) {
        tasks.push(
          ...i.action_tasks.map((t) => ({
            itemId: i.item_id,
            itemName: i.name,
            id: t.task_id,
            name: t.name,
            dueDate: new Date(t.due_date),
            status: t.status,
            assignee: t.assignee_user_name,
            notes: t.notes,
            isExpired: ActionPlanUtils.isExpired(t),
            expiredTooltip: ActionPlanUtils.getExpiredLabel(t),
            isExpanded: false,
          }))
        );
      }
    });

    return tasks;
  }

  protected sorting = (a: TaskDataItem, b: TaskDataItem): number => {
    let result = 0;
    switch (this.sort.active) {
      case 'name':
        result = a.name.localeCompare(b.name);
        break;
      case 'due_date':
        result = a.dueDate.getTime() - b.dueDate.getTime();
        break;
      case 'status':
        result = this.getStatusValue(a) - this.getStatusValue(b);
        break;
      case 'assignee':
        // optional field, empties are last
        if (!a.assignee) {
          result = 1;
        } else if (!b.assignee) {
          result = -1;
        } else {
          result = a.assignee.localeCompare(b.assignee);
        }
        break;
      case 'item_name':
        result = a.itemName.localeCompare(b.itemName);
        break;
    }

    const direction = this.sort.direction === 'asc' ? 1 : -1;
    return result * direction;
  };
}
